import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Button, Divider, CircularProgress,
  Alert, Radio, RadioGroup, FormControlLabel, FormControl,
  Paper, Chip, LinearProgress, Table, TableHead, TableRow,
  TableCell, TableBody
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import api from '../axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.conectachurch.com.br';

// --- Renderizadores de Bloco ---
function BlocoTexto({ bloco }) {
  return (
    <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
      {bloco.conteudo}
    </Typography>
  );
}

function BlocoTitulo({ bloco }) {
  return (
    <Typography variant={bloco.nivel === 2 ? 'h5' : 'h6'} sx={{ mt: 3, mb: 1, fontWeight: 700 }}>
      {bloco.conteudo}
    </Typography>
  );
}

function BlocoVideo({ bloco }) {
  const url = bloco.url || '';
  // Suporte YouTube embed
  const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([a-zA-Z0-9_-]{11})/);
  if (ytMatch) {
    return (
      <Box sx={{ position: 'relative', paddingBottom: '56.25%', height: 0, mb: 2 }}>
        <iframe
          title="video"
          src={`https://www.youtube.com/embed/${ytMatch[1]}`}
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0, borderRadius: 8 }}
          allowFullScreen
        />
      </Box>
    );
  }
  return (
    <Box sx={{ mb: 2 }}>
      <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
    </Box>
  );
}

function BlocoTabela({ bloco }) {
  if (!bloco.linhas || !bloco.linhas.length) return null;
  const cabecalho = bloco.cabecalho || [];
  return (
    <Box sx={{ overflowX: 'auto', mb: 2 }}>
      <Table size="small" sx={{ border: '1px solid #ddd', borderRadius: 1 }}>
        {cabecalho.length > 0 && (
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              {cabecalho.map((c, i) => <TableCell key={i} sx={{ fontWeight: 700 }}>{c}</TableCell>)}
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          {bloco.linhas.map((linha, i) => (
            <TableRow key={i}>
              {linha.map((cel, j) => <TableCell key={j}>{cel}</TableCell>)}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function BlocoImagem({ bloco }) {
  return (
    <Box sx={{ mb: 2, textAlign: 'center' }}>
      <img src={bloco.url} alt={bloco.legenda || 'imagem'} style={{ maxWidth: '100%', borderRadius: 8 }} />
      {bloco.legenda && <Typography variant="caption" color="text.secondary">{bloco.legenda}</Typography>}
    </Box>
  );
}

function renderBloco(bloco, idx) {
  switch (bloco.tipo) {
    case 'titulo': return <BlocoTitulo key={idx} bloco={bloco} />;
    case 'texto': return <BlocoTexto key={idx} bloco={bloco} />;
    case 'video': return <BlocoVideo key={idx} bloco={bloco} />;
    case 'tabela': return <BlocoTabela key={idx} bloco={bloco} />;
    case 'imagem': return <BlocoImagem key={idx} bloco={bloco} />;
    default: return null;
  }
}

// --- Quiz ---
function QuizSection({ quiz, aulaId }) {
  const [respostas, setRespostas] = useState({});
  const [resultado, setResultado] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [jaRespondeu, setJaRespondeu] = useState(null); // null = carregando

  useEffect(() => {
    api.get(`/aulas/${aulaId}/quiz/meu-resultado`)
      .then(res => setJaRespondeu(res.data))
      .catch(() => setJaRespondeu({ respondeu: false }));
  }, [aulaId]);

  const handleEnviar = async () => {
    if (Object.keys(respostas).length < quiz.length) {
      alert('Responda todas as perguntas antes de enviar.');
      return;
    }
    setEnviando(true);
    try {
      const payload = {
        respostas: Object.entries(respostas).map(([pid, resp]) => ({
          pergunta_id: parseInt(pid),
          resposta: resp
        }))
      };
      const res = await api.post(`/aulas/${aulaId}/quiz/responder`, payload);
      setResultado(res.data);
      setJaRespondeu({ respondeu: true, acertos: res.data.acertos, total: res.data.total, respostas: res.data.resultado });
    } catch (e) {
      alert('Erro ao enviar respostas.');
    }
    setEnviando(false);
  };

  if (!quiz || quiz.length === 0) return null;

  // Mapa de resultado por pergunta_id
  const mapaResultado = resultado
    ? Object.fromEntries(resultado.resultado.map(r => [r.pergunta_id, r]))
    : jaRespondeu?.respostas
    ? Object.fromEntries(jaRespondeu.respostas.map(r => [r.pergunta_id, r]))
    : null;

  const mostrarResultado = resultado || (jaRespondeu && jaRespondeu.respondeu);
  const acertos = resultado ? resultado.acertos : jaRespondeu?.acertos;
  const total = resultado ? resultado.total : jaRespondeu?.total;

  return (
    <Box sx={{ mt: 4 }}>
      <Divider sx={{ mb: 3 }} />
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>📝 Questionário da Lição</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {quiz.length} perguntas sobre o conteúdo da aula
      </Typography>

      {mostrarResultado && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: acertos === total ? '#e8f5e9' : '#fff3e0', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            {acertos === total
              ? <CheckCircleIcon color="success" />
              : <CancelIcon color="warning" />}
            <Typography variant="h6" fontWeight={700}>
              Resultado: {acertos}/{total} corretas
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={(acertos / total) * 100}
            color={acertos === total ? 'success' : 'warning'}
            sx={{ height: 8, borderRadius: 4 }}
          />
          {jaRespondeu?.respondeu && !resultado && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Você já respondeu este questionário.
            </Typography>
          )}
        </Paper>
      )}

      {quiz.map((p, idx) => {
        const res = mapaResultado ? mapaResultado[p.id] : null;
        return (
          <Paper key={p.id} sx={{ p: 2, mb: 2, borderRadius: 2, border: res
            ? res.correta ? '1.5px solid #4caf50' : '1.5px solid #f44336'
            : '1px solid #e0e0e0' }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              {idx + 1}. {p.pergunta}
            </Typography>
            <FormControl component="fieldset" disabled={!!mostrarResultado}>
              <RadioGroup
                value={respostas[p.id] || (res ? res.resposta_dada : '')}
                onChange={e => setRespostas(prev => ({ ...prev, [p.id]: e.target.value }))}
              >
                {['a', 'b', 'c', 'd'].map(op => {
                  const label = p[`opcao_${op}`];
                  const isCorrect = res && res.resposta_correta === op;
                  const isWrong = res && res.resposta_dada === op && !res.correta;
                  return (
                    <FormControlLabel
                      key={op}
                      value={op}
                      control={<Radio size="small" />}
                      label={
                        <span style={{ color: isCorrect ? '#2e7d32' : isWrong ? '#c62828' : 'inherit', fontWeight: isCorrect ? 700 : 400 }}>
                          <strong>{op.toUpperCase()})</strong> {label}
                          {isCorrect && <CheckCircleIcon fontSize="small" sx={{ ml: 1, verticalAlign: 'middle', color: '#4caf50' }} />}
                          {isWrong && <CancelIcon fontSize="small" sx={{ ml: 1, verticalAlign: 'middle', color: '#f44336' }} />}
                        </span>
                      }
                    />
                  );
                })}
              </RadioGroup>
            </FormControl>
          </Paper>
        );
      })}

      {!mostrarResultado && (
        <Button
          variant="contained"
          color="success"
          size="large"
          onClick={handleEnviar}
          disabled={enviando || Object.keys(respostas).length < quiz.length}
          sx={{ mt: 1, fontWeight: 700 }}
        >
          {enviando ? <CircularProgress size={22} color="inherit" /> : 'Enviar Respostas'}
        </Button>
      )}
    </Box>
  );
}

// --- Página Principal ---
export default function AulaDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [aula, setAula] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  useEffect(() => {
    api.get(`/aulas/${id}/detalhe`)
      .then(res => { setAula(res.data); setLoading(false); })
      .catch(() => { setErro('Aula não encontrada.'); setLoading(false); });
  }, [id]);

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
      <CircularProgress />
    </Box>
  );

  if (erro) return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Alert severity="error">{erro}</Alert>
    </Container>
  );

  const dataFormatada = aula.data
    ? new Date(aula.data).toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '-';

  return (
    <Container maxWidth="md" sx={{ mt: 3, mb: 6 }}>
      {/* Botão voltar */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        fullWidth
        sx={{ mb: 2, textTransform: 'none' }}
      >
        Voltar
      </Button>

      {/* Foto de capa */}
      {aula.tem_foto && (
        <Box sx={{ mb: 3, borderRadius: 3, overflow: 'hidden', maxHeight: 320, display: 'flex', justifyContent: 'center', bgcolor: '#000' }}>
          <img
            src={`${API_BASE_URL}/aulas/${id}/foto`}
            alt="Capa da aula"
            style={{ width: '100%', objectFit: 'cover', maxHeight: 320 }}
          />
        </Box>
      )}

      {/* Cabeçalho */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Chip label={`Lição ${aula.numero_licao}: ${aula.licao}`} color="primary" />
          {aula.tipo_aula && (
            <Chip
              label={aula.tipo_aula}
              sx={{ bgcolor: aula.tipo_aula === 'PRESENCIAL' ? '#4caf50' : '#2196f3', color: '#fff' }}
            />
          )}
          {aula.turma && <Chip label={aula.turma} variant="outlined" />}
        </Box>
        <Typography variant="caption" color="text.secondary">{dataFormatada}</Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Links */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {aula.link_streaming && (
          <Button variant="outlined" color="primary" href={aula.link_streaming} target="_blank" fullWidth>
            🎥 Acessar Aula Online
          </Button>
        )}
        {aula.pdf_path && (
          <Button variant="outlined" color="secondary" href={`${API_BASE_URL}/aulas/${id}/pdf`} target="_blank" fullWidth>
            📄 Ver PDF da Lição
          </Button>
        )}
      </Box>

      {/* Conteúdo em blocos */}
      {aula.blocos && aula.blocos.length > 0 && (
        <Box sx={{ mb: 2 }}>
          {aula.blocos.map((bloco, idx) => renderBloco(bloco, idx))}
        </Box>
      )}

      {aula.blocos && aula.blocos.length === 0 && (
        <Typography color="text.secondary" sx={{ mb: 3, fontStyle: 'italic' }}>
          O conteúdo desta aula ainda não foi publicado.
        </Typography>
      )}

      {/* Quiz */}
      <QuizSection quiz={aula.quiz} aulaId={id} />
    </Container>
  );
}
